import { useEffect, useMemo } from "react"

import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { getAddonVariantKeys } from "shared/lib/product-configuration/getAddonVariantKeys"
import {
  getHighestMatchingProductAddonBaseVariant,
  getProductAddonObj,
  loadProductAddons
} from "shared/lib/product-obj/loadProductAddons"
import { useForcedUpdate } from "shared/lib/useForcedUpdate"
import { Maybe } from "shared/types/Maybe"
import { ProductAddon } from "shared/types/ProductAddon"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"
import { RJSFSchema } from "shared/types/RJSF"

interface UseProductConfigurationAddonsProps {
  addons: RJSFSchema | undefined | null
  addonVariantKeys: string[] | null | undefined
  loadingFieldID?: string | null | undefined
  formData?: Maybe<ProductConfigurationFormData["addons"]>
  productObj?: ProductObj | null | undefined
}

function hasContent(schema: RJSFSchema) {
  return !!schema.properties?.product || !!schema.properties?.addons
}

/**
 * Custom hook for handling product configuration addons.
 *
 * @param {RJSFSchema} args.addons - The JSON schema used to configure the addons of the product.
 * @param {RJSFSchema} args.loadingFieldID - The ID of the field that triggered the current request. If it corresponds to the the ID of the addon a loader is displayed.
 * @param {ProductConfigurationFormData["addons"]} args.formData - Optional. The formData used to configure the addons of the product.
 * @param {ProductObj} args.productObj - Optional. The Scrivito Obj representing the product.
 * @returns An array of ProductAddon objects.
 */
export function useProductConfigurationAddons({
  addons,
  loadingFieldID,
  addonVariantKeys,
  formData,
  productObj
}: UseProductConfigurationAddonsProps) {
  const [state, forceUpdate] = useForcedUpdate()

  const variantKeyByConfigurationID = useMemo(
    () => getAddonVariantKeys(addons, addonVariantKeys),
    [addons, addonVariantKeys]
  )

  useEffect(() => {
    if (productObj) {
      loadProductAddons(productObj).then(() => forceUpdate())
    }
  }, [productObj])

  return useMemo(() => {
    if (!addons?.properties) {
      return [] as ProductAddon[]
    }
    return Object.entries(addons?.properties).map(
      ([configurationID, schema]: [string, RJSFSchema]) => {
        const addonFormData = formData?.[configurationID]
        return {
          key: configurationID,
          isSelected: addonFormData?.value,
          isConfigurable:
            loadingFieldID === configurationID ? undefined : hasContent(schema),
          schema,
          obj: getProductAddonObj({ productObj, configurationID }),
          baseVariant: getHighestMatchingProductAddonBaseVariant({
            productObj,
            addonVariantKey: variantKeyByConfigurationID[configurationID],
            configurationID
          }),
          formData: addonFormData
        }
      }
    )
  }, [state, loadingFieldID, addons, formData, productObj])
}
